import { atom } from "jotai"
// eslint-disable-next-line no-restricted-imports -- locale-aware date display
import i18n from "i18next"

import {
	allCamerasAtom,
	allFileFormatsAtom,
	cullingInfoAtom,
	metadataAtom,
	photosAtom,
} from "@/store/photo/photos-state"
import { homeSectionAtom, projectActionToastAtom } from "@/store/ui-views-state"
import { loadingStateAtom } from "@/store/app-state"
import { store } from "@/store/store"
import { CatalogManager, ProjectInfoRecord } from "@/lib/catalog"
import { PathInfo } from "@/storage/file-system"
import { atomWithMutation, atomWithQuery } from "jotai-tanstack-query"
import { queryClient } from "../../api/constants"
import { atomFamily, atomWithStorage } from "jotai/utils"
import { safeJSONLocalStorage } from "@/utils/safe-jotai-storage"
import {
	ProjectGrouping,
	ProjectItem,
	ProjectService,
	getUpdatableProjectFields,
} from "@/store/project/projects-service"
import { clearPhotoLoadingState, photoLoadingStateAtom } from "../editor/photo-loading"

import { currentStyleAtom } from "../style/style-state"
import { DisplayGroup, PhotoFlagStatus, PhotoTag, Rating, Visibility } from "@/lib/catalog/types"
import { projectIsDeleted } from "@/api/supression"
import { ColorTag } from "@/utils/exif"
import { resetToDefaultAppState } from "../undoable-state"
import { startOfMonth } from "date-fns/fp"
import { isDefined } from "@/utils/object"
import { DataSource } from "../data-source"
import { isAuthReadyAtom } from "@/store/auth-state"
import { MainMaskCache } from "@/storage/mask-cache"
import { filesAtom } from "../files-state"
import { lastSelectionFromClickAtom } from "../photo/photos-selection-state"
import { isNetworkConnectivityError } from "@/api/network-behavior"
import { Sentry } from "@/telemetry/sentry"

export const normalizeFilterArray = <T>(value: unknown): T[] | undefined => {
	if (value === undefined || value === null) {
		return undefined
	}

	if (Array.isArray(value)) {
		return value as T[]
	}

	if (value instanceof Set) {
		return Array.from(value) as T[]
	}

	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value)
			if (Array.isArray(parsed)) {
				return parsed as T[]
			}
		} catch {
			return undefined
		}
	}

	return undefined
}

export const isHomeButtonEnabledAtom = atom(true)

export const baseProjectAtom = atom<ProjectItem | null>(null)

const fetchProjectsAtom = atomWithQuery<ProjectItem[]>((get) => ({
	queryKey: ["projects"],
	queryFn: ProjectService.listProjects,
	enabled: get(isAuthReadyAtom),
}))

export const createProjectAtom = atomWithMutation(() => ({
	mutationKey: ["project", "create"],
	mutationFn: ProjectService.createProject,
	onSuccess: async () => {
		await queryClient.invalidateQueries({ queryKey: ["projects"] })
		//InvalidateQueries does not trigger immediate refetch
		//if we need latest projects while inside a project
		//we need to subscribe to projectsAtom to get refreshed data properly
	},
}))

export const projectByIdMapAtom = atom((get) => {
	const { data: projects } = get(projectsAtom)
	return Object.fromEntries(projects.map((project) => [project.id, project]))
})

export const syncProject = async (projectId: string, mutations: Partial<ProjectItem>) => {
	if (!projectIsDeleted(projectId)) {
		await ProjectService.updateProject(
			{ id: projectId, source: "backend" as DataSource },
			mutations,
		)
		await queryClient.invalidateQueries({ queryKey: ["projects"] })
	}
}

export const projectsAtom = atom<{ data: ProjectItem[]; isInitialLoading: boolean }>((get) => {
	const { data, isInitialLoading, isError } = get(fetchProjectsAtom)

	if (data && !isError) {
		return { data, isInitialLoading }
	}

	return {
		data: [],
		isInitialLoading,
	}
})

export const ProjectSortOptions = ["eventDateAscending", "eventDateDescending"]

export const projectSearchInputAtom = atom<string>("")

export type ProjectSortOption = (typeof ProjectSortOptions)[number]
export const projectsSortingAtom = atom<ProjectSortOption>("eventDateDescending")

export const sortedProjectsAtom = atom((get) => {
	let { data: projects } = get(projectsAtom)
	const sortOption = get(projectsSortingAtom)
	const filter = get(projectSearchInputAtom).toLowerCase()

	if (filter.length > 0) {
		projects = projects.filter((project) => project.name.toLowerCase().includes(filter))
	}

	const projectsByMonth: Record<string, ProjectGrouping> = {}
	switch (sortOption) {
		case "eventDateAscending":
			projects.sort((a, b) => a.eventStartedAt.getTime() - b.eventStartedAt.getTime())
			break
		case "eventDateDescending":
			projects.sort((a, b) => b.eventStartedAt.getTime() - a.eventStartedAt.getTime())
			break
	}

	const getMonthKey = (project: ProjectItem) => {
		return startOfMonth(project.eventStartedAt).toISOString()
	}

	const getMonthTitle = (project: ProjectItem) => {
		return project.eventStartedAt.toLocaleString(i18n.language, {
			month: "long",
			year: "numeric",
		})
	}

	for (const project of projects) {
		const key = getMonthKey(project)
		const existingGrouping = projectsByMonth[key]
		if (existingGrouping) {
			existingGrouping.data.push(project)
		} else {
			const title = getMonthTitle(project)
			projectsByMonth[key] = {
				title,
				data: [project],
			}
		}
	}

	const groupedProjects = Object.keys(projectsByMonth)
		.map((key) => projectsByMonth[key])
		.filter(isDefined)

	return groupedProjects
})

export const isManualModeAtom = atom<boolean>((get) => get(baseProjectAtom)?.isManual === true)

export const isCullingModeAtom = atom((get) => {
	return get(baseProjectAtom)?.isCulling === true
})

export const isAIEditingModeAtom = atom(
	(get) => get(isManualModeAtom) !== true && get(isCullingModeAtom) !== true,
)

export const setIsCulling = async (isCulling: boolean, projectId?: string) => {
	const project = store.get(projectAtom)
	if (project) {
		await updateCurrentProject({ isCulling })
	} else if (projectId) {
		await CatalogManager.updateProjectInfo(projectId, { isCulling })
	}
}

export const projectAtom = atom(
	(get) => get(baseProjectAtom),
	(get, set, project: ProjectItem | null) => {
		const currentProject = get(baseProjectAtom)
		if (currentProject?.id !== project?.id) {
			if (project) {
				MainMaskCache.setContext({ projectId: project.id })
			} else {
				MainMaskCache.clearContext()
			}
		}
		if (currentProject === project) {
			return
		}
		if (!project) {
			set(homeSectionAtom, "projects")

			resetToDefaultAppState()

			set(loadingStateAtom, { status: null })
			set(photoLoadingStateAtom, "initial")

			set(currentStyleAtom, null)
			set(projectActionToastAtom, null)
			set(metadataAtom, {})
			set(cullingInfoAtom, {})
			set(filesAtom, {})
			set(photosAtom, {})

			set(lastSelectionFromClickAtom, false)

			clearPhotoLoadingState()
		}
		set(baseProjectAtom, project)
	},
)

export const updateCurrentProject = async (updates: Partial<ProjectItem>) => {
	const project = store.get(projectAtom)
	if (!project) {
		return
	}

	const updatedProject = {
		...project,
		...updates,
	}

	//Sync to BE
	let updatesForBE = getUpdatableProjectFields(updates)
	if (project.isSampleProject) {
		// Sample projects are read-only on the backend except for eventStartedAt/orderScore.
		updatesForBE = Object.fromEntries(
			Object.entries(updatesForBE).filter(
				([key]) => key === "eventStartedAt" || key === "orderScore",
			),
		)
	}
	if (Object.keys(updatesForBE).length > 0) {
		await syncProject(project.id, updatesForBE)
	}

	store.set(projectAtom, updatedProject)

	//Sync to current catalog
	await CatalogManager.storeProjectInfo({
		record: projectItemToProjectInfo(updatedProject),
	})
}

const updateCurrentProjectInBackground = (updates: Partial<ProjectItem>) => {
	void updateCurrentProject(updates).catch((error) => {
		if (isNetworkConnectivityError(error)) {
			return
		}

		Sentry.captureException(error, {
			tags: {
				source: "projects-state:updateCurrentProjectInBackground",
			},
		})
	})
}

export function projectItemToProjectInfo(item: ProjectItem): ProjectInfoRecord {
	return {
		id: item.id,
		name: item.name,
		styleId: item.styleId ?? null,
		sortOption: item.sortOption ?? null,
		needsSetup: item.needsSetup ?? null,
		isManual: item.isManual ?? null,
		isCulling: item.isCulling ?? null,
		selectedTags: item.selectedTags ?? null,
		suggestedKeeperCount: item.suggestedKeeperCount ?? null,
		ratingFilters: normalizeFilterArray(item.ratingFilters) ?? null,
		pathFilters: normalizeFilterArray(item.pathFilters) ?? null,
		colorFilters: normalizeFilterArray(item.colorFilters) ?? null,
		cameraFilters: normalizeFilterArray(item.cameraFilters) ?? null,
		flagFilters: normalizeFilterArray(item.flagFilters) ?? null,
		fileFormatFilters: normalizeFilterArray(item.fileFormatFilters) ?? null,
		visibilityFilters: normalizeFilterArray(item.visibilityFilters) ?? null,
		isExpressSetup: item.isExpressSetup,
		displayGroup: item.displayGroup ?? null,
		publishCollectionId: item.publishCollectionId,
		publishGalleryId: item.publishGalleryId ?? null,
		cullingSettings: item.cullingSettings,
	}
}

export const pendingFolderPermissionsAtom = atom<boolean>(false)

export const isCalibrationAtom = atom(
	(get) => {
		const project = get(projectAtom)
		return !!project?.needsSetup
	},
	(get, _, isCalibration: boolean) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ needsSetup: isCalibration })
		}
	},
)

export const projectPathsAtom = atom<PathInfo[]>([])

export const projectListMonthStateAtomFamily = atomFamily((month: string) =>
	atomWithStorage(`group_expanded:${month}`, true, safeJSONLocalStorage<boolean>()),
)

export type ProjectsDisplayMode = "grid" | "list"
export const projectsDisplayAtom = atomWithStorage<ProjectsDisplayMode>(
	"projectsDisplay",
	"grid",
	safeJSONLocalStorage<ProjectsDisplayMode>(),
)

export const selectedTagsAtom = atom(
	(get) => {
		const project = get(projectAtom)
		return project?.selectedTags
			? new Set<PhotoTag>(project?.selectedTags)
			: new Set<PhotoTag>()
	},
	(get, _, selectedTags: Set<PhotoTag>) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ selectedTags: [...selectedTags] })
		}
	},
)

export const ratingFiltersAtom = atom(
	(get) => {
		const project = get(projectAtom)
		const ratingFilters = normalizeFilterArray<Rating>(project?.ratingFilters) ?? []
		return new Set<Rating>(ratingFilters)
	},
	(get, _, ratingFilters: Set<Rating>) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ ratingFilters: [...ratingFilters] })
		}
	},
)

export const pathFiltersAtom = atom(
	(get) => {
		const project = get(projectAtom)
		const pathFilters = normalizeFilterArray<string>(project?.pathFilters) ?? []
		return new Set<string>(pathFilters)
	},
	(get, _, pathFilters: Set<string>) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ pathFilters: [...pathFilters] })
		}
	},
)

export const colorFiltersAtom = atom(
	(get) => {
		const project = get(projectAtom)
		const colorFilters = normalizeFilterArray<ColorTag>(project?.colorFilters) ?? []
		return new Set<ColorTag>(colorFilters)
	},
	(get, _, colorFilters: Set<ColorTag>) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ colorFilters: [...colorFilters] })
		}
	},
)

export const cameraFiltersAtom = atom(
	(get) => {
		const project = get(projectAtom)
		const cameraFilters = normalizeFilterArray<string>(project?.cameraFilters) ?? []
		return new Set<string>(cameraFilters)
	},
	(get, _, cameraFilters: Set<string>) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ cameraFilters: [...cameraFilters] })
		}
	},
)

export const flagFiltersAtom = atom(
	(get) => {
		const project = get(projectAtom)
		const flagFilters = normalizeFilterArray<PhotoFlagStatus>(project?.flagFilters) ?? []
		return new Set<PhotoFlagStatus>(flagFilters)
	},
	(get, _, flagFilters: Set<PhotoFlagStatus>) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ flagFilters: [...flagFilters] })
		}
	},
)

export const fileFormatFiltersAtom = atom(
	(get) => {
		const project = get(projectAtom)
		const fileFormatFilters = normalizeFilterArray<string>(project?.fileFormatFilters) ?? []
		return new Set<string>(fileFormatFilters)
	},
	(get, _, fileFormatFilters: Set<string>) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ fileFormatFilters: [...fileFormatFilters] })
		}
	},
)

export const visibilityFiltersAtom = atom(
	(get) => {
		const project = get(projectAtom)
		const visibilityFilters = normalizeFilterArray<Visibility>(project?.visibilityFilters) ?? []
		return new Set<Visibility>(visibilityFilters)
	},
	(get, _, visibilityFilters: Set<Visibility>) => {
		const project = get(projectAtom)
		if (project) {
			updateCurrentProjectInBackground({ visibilityFilters: [...visibilityFilters] })
		}
	},
)

export const displayGroupAtom = atom(
	(get) => {
		const project = get(projectAtom)
		return project?.displayGroup ?? "all"
	},
	(get, set, displayGroup: DisplayGroup) => {
		const project = get(projectAtom)
		if (project) {
			set(projectAtom, { ...project, displayGroup })
			updateCurrentProjectInBackground({ displayGroup })
		}
	},
)

export const selectDisplayGroup = (selectedGroup: DisplayGroup) => {
	const current = store.get(displayGroupAtom)

	if (current == selectedGroup) {
		//unselect
		store.set(displayGroupAtom, "all")
	} else {
		store.set(displayGroupAtom, selectedGroup)
	}
}

export const selectRatingFilter = (rating: Rating) => {
	const ratingFilters = store.get(ratingFiltersAtom)

	if (ratingFilters.has(rating)) {
		ratingFilters.delete(rating)
	} else {
		ratingFilters.add(rating)
	}
	store.set(ratingFiltersAtom, new Set(ratingFilters))
}

export const selectColorFilter = (color: ColorTag) => {
	const colorFilters = store.get(colorFiltersAtom)
	if (colorFilters.has(color)) {
		colorFilters.delete(color)
	} else {
		colorFilters.add(color)
	}
	store.set(colorFiltersAtom, new Set(colorFilters))
}

export const selectFlagFilter = (flag: PhotoFlagStatus) => {
	const flagFilters = store.get(flagFiltersAtom)
	if (flagFilters.has(flag)) {
		flagFilters.delete(flag)
	} else {
		flagFilters.add(flag)
	}
	store.set(flagFiltersAtom, new Set(flagFilters))
}

export const selectPathFilter = (path: string) => {
	const pathFilters = store.get(pathFiltersAtom)
	const allPaths = store.get(projectPathsAtom).map((p) => p.path)
	const allSubpaths = allPaths.filter((p) => p === path || p.startsWith(path + "/"))

	let updated = new Set(pathFilters)
	if (!pathFilters.has(path)) {
		updated = new Set([...pathFilters, ...allSubpaths])
	} else {
		for (const subpath of allSubpaths) {
			updated.delete(subpath)
		}
	}

	store.set(pathFiltersAtom, updated)
}

export const selectCameraFilter = (camera: string) => {
	const cameraFilters = store.get(cameraFiltersAtom)
	const allCameras = store.get(allCamerasAtom)
	const isAllSelected = cameraFilters.size === 0

	let updated = cameraFilters
	if (camera === "all") {
		if (isAllSelected) {
			updated = new Set<string>(Object.keys(allCameras))
		} else {
			updated = new Set()
		}
		return
	}

	if (cameraFilters.has(camera)) {
		updated.delete(camera)
	} else {
		updated.add(camera)
	}

	store.set(cameraFiltersAtom, updated)
}

export const selectFileFormatFilter = (format: string) => {
	const fileFormatFilters = store.get(fileFormatFiltersAtom)
	const allFormats = store.get(allFileFormatsAtom)
	const isAllSelected = fileFormatFilters.size === 0

	let updated = fileFormatFilters
	if (format === "all") {
		if (isAllSelected) {
			updated = new Set<string>(Object.keys(allFormats))
		} else {
			updated = new Set()
		}
		return
	}

	if (fileFormatFilters.has(format)) {
		updated.delete(format)
	} else {
		updated.add(format)
	}

	store.set(fileFormatFiltersAtom, updated)
}

export const selectVisibilityFilter = (option: Visibility) => {
	const visibilityFilters = store.get(visibilityFiltersAtom)

	if (visibilityFilters.has(option)) {
		visibilityFilters.delete(option)
	} else {
		visibilityFilters.add(option)
	}

	store.set(visibilityFiltersAtom, new Set(visibilityFilters))
}

export const setupProjectsSubscribers = () => {
	store.sub(projectsAtom, () => {
		//This is to keep projects up to date throughout the entire app
	})
}
